import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Settings as SettingsIcon, FileText, Plus, Trash2, Upload, Download, Users, CheckCircle, XCircle, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import api, { UPLOAD_TIMEOUT } from '../lib/api';
import { getManilaNow, getManilaTodayStr } from '../lib/dateUtils';
import { compressImage } from '../lib/imageCompression';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import CEODashboard from './CEODashboard';
import PlatformAnalytics from './PlatformAnalytics';
import MarketingDashboard from './MarketingDashboard';
import CommunityBoardAdmin from './CommunityBoardAdmin';
import EmployerActivityLog from './EmployerActivityLog';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

// Manila-local YYYY-MM-DD string `days` before now — mirrors getManilaTodayStr() but with an
// offset, so report/date-range defaults anchor on Asia/Manila, not the admin's device timezone.
const manilaDaysAgoStr = (days) => {
  const d = getManilaNow();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getMilestoneLabel = (milestone) => {
  if (!milestone) return 'Unknown';
  if (milestone === 'profile_complete') return 'Profile P20';
  if (milestone === 'verification') return 'ID Verify P30';
  if (milestone === 'employer_verified') return 'Employer Verified P300';
  if (milestone === 'employer_first_job') return '1st Job P100';
  if (milestone === 'employer_10plus_employees') return '10+ Emp P200';
  if (milestone.startsWith('employer_monthly_bonus_')) return 'Monthly Bonus P200';
  return milestone;
};

const ReferralsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [referrerFilter, setReferrerFilter] = useState('');

  const fetchData = async (p = page) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;
      let url = `/api/admin/referrals?page=${p}&per_page=50`;
      if (statusFilter) url += `&status_filter=${statusFilter}`;
      if (referrerFilter) url += `&referrer_id=${referrerFilter}`;
      const res = await api.get(url, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setSelectedIds([]); fetchData(); }, [page, statusFilter, referrerFilter]);

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one payout');
      return;
    }
    setActionLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) { toast.error('Session expired'); return; }
      const endpoint = action === 'paid' ? '/api/admin/referrals/mark-paid' : '/api/admin/referrals/cancel';
      await api.post(endpoint, { payout_ids: selectedIds }, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      toast.success(action === 'paid' ? 'Marked as paid' : 'Cancelled');
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      toast.error('Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <Card><CardContent className="p-8 text-center">Loading referral data...</CardContent></Card>;
  if (!data) return <Card><CardContent className="p-8 text-center">No referral data available</CardContent></Card>;

  const { summary, payouts, ambassadors, pagination } = data;
  const pendingPayouts = payouts.filter(p => p.status === 'pending');

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <Users className="mx-auto mb-2 text-primary" size={24} />
            <p className="text-3xl font-bold">{summary.ambassador_count}</p>
            <p className="text-sm text-muted-foreground">Ambassadors</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <DollarSign className="mx-auto mb-2 text-amber-500" size={24} />
            <p className="text-3xl font-bold">P{(summary.pending_amount || 0).toFixed(0)}</p>
            <p className="text-sm text-muted-foreground">Pending ({summary.pending_count})</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <CheckCircle className="mx-auto mb-2 text-green-500" size={24} />
            <p className="text-3xl font-bold">P{(summary.paid_amount || 0).toFixed(0)}</p>
            <p className="text-sm text-muted-foreground">Paid ({summary.paid_count})</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <FileText className="mx-auto mb-2 text-blue-500" size={24} />
            <p className="text-3xl font-bold">{summary.total_payouts}</p>
            <p className="text-sm text-muted-foreground">Total Payouts</p>
          </CardContent>
        </Card>
      </div>

      {/* Ambassadors List */}
      <Card>
        <CardHeader>
          <CardTitle>Ambassadors</CardTitle>
        </CardHeader>
        <CardContent>
          {ambassadors.length === 0 ? (
            <p className="text-muted-foreground text-sm">No ambassadors yet. Enable ambassador status from CEO War Room user profiles.</p>
          ) : (
            <div className="space-y-2">
              {ambassadors.map(amb => (
                <div key={amb.id} className="flex items-center justify-between gap-2 bg-amber-50 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant="outline" className="text-amber-700 border-amber-200 shrink-0">
                      {amb.name}
                    </Badge>
                    <span className="text-xs font-mono text-muted-foreground break-all">www.boholtrabaho.com/auth?ref={amb.referral_code}</span>
                  </div>
                  <span className="text-xs font-mono shrink-0">
                    {amb.gcash_number ? (
                      <span className="text-green-700">GCash: {amb.gcash_number}</span>
                    ) : (
                      <span className="text-red-400 italic">No GCash</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Payouts Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span>Pending Payouts ({summary.pending_count})</span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleBulkAction('cancel')}
                disabled={selectedIds.length === 0 || actionLoading}
              >
                <XCircle size={14} className="mr-1" /> Cancel Selected
              </Button>
              <Button
                size="sm"
                onClick={() => handleBulkAction('paid')}
                disabled={selectedIds.length === 0 || actionLoading}
              >
                <CheckCircle size={14} className="mr-1" /> {actionLoading ? 'Processing...' : 'Mark Paid'}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingPayouts.length === 0 ? (
            <p className="text-muted-foreground text-sm">No pending payouts.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === pendingPayouts.length && pendingPayouts.length > 0}
                      onChange={(e) => {
                        setSelectedIds(e.target.checked ? pendingPayouts.map(p => p.id) : []);
                      }}
                    />
                  </TableHead>
                  <TableHead>Referrer</TableHead>
                  <TableHead className="hidden md:table-cell">Code</TableHead>
                  <TableHead className="hidden md:table-cell">Referred</TableHead>
                  <TableHead className="hidden lg:table-cell">Milestone</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead className="hidden md:table-cell">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingPayouts.map(payout => (
                  <TableRow key={payout.id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(payout.id)}
                        onChange={(e) => {
                          setSelectedIds(prev =>
                            e.target.checked ? [...prev, payout.id] : prev.filter(id => id !== payout.id)
                          );
                        }}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      {payout.referrer?.name || 'Unknown'}
                      <p className="text-xs text-muted-foreground md:hidden">
                        → {payout.referred?.name || 'Unknown'} · {getMilestoneLabel(payout.milestone)}
                      </p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell font-mono text-xs">{payout.referrer?.referral_code || '-'}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      {payout.referred?.name || 'Unknown'}
                      {payout.referred?.verified && (
                        <Badge variant="outline" className="ml-1 text-xs bg-green-50 text-green-700">Verified</Badge>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <Badge variant="outline" className="text-xs">
                        {getMilestoneLabel(payout.milestone)}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-semibold">P{parseFloat(payout.amount_php || 0).toFixed(0)}</TableCell>
                    <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                      {new Date(payout.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* All Payouts — Filter + Table + Pagination */}
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span>All Payouts</span>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={referrerFilter} onValueChange={(v) => { setReferrerFilter(v === 'all' ? '' : v); setPage(1); }}>
                <SelectTrigger className="w-[160px] h-8 text-xs">
                  <SelectValue placeholder="All Referrers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Referrers</SelectItem>
                  {ambassadors.map(amb => (
                    <SelectItem key={amb.id} value={amb.id}>{amb.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {['', 'pending', 'paid', 'cancelled'].map(f => (
                <Button
                  key={f || 'all'}
                  size="sm"
                  variant={statusFilter === f ? 'default' : 'outline'}
                  onClick={() => { setStatusFilter(f); setPage(1); }}
                >
                  {f || 'All'}
                </Button>
              ))}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {payouts.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-4">No payouts found.</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Referrer</TableHead>
                    <TableHead className="hidden md:table-cell">Referred</TableHead>
                    <TableHead className="hidden lg:table-cell">Milestone</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden md:table-cell">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payouts.map(payout => (
                    <TableRow key={payout.id}>
                      <TableCell className="font-medium">
                        {payout.referrer?.name || 'Unknown'}
                        <p className="text-xs text-muted-foreground md:hidden">
                          → {payout.referred?.name || 'Unknown'} · {getMilestoneLabel(payout.milestone)}
                        </p>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{payout.referred?.name || 'Unknown'}</TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <Badge variant="outline" className="text-xs">
                          {getMilestoneLabel(payout.milestone)}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-semibold">P{parseFloat(payout.amount_php || 0).toFixed(0)}</TableCell>
                      <TableCell>
                        <Badge className={
                          payout.status === 'paid' ? 'bg-green-100 text-green-700' :
                          payout.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }>
                          {payout.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                        {payout.paid_at ? new Date(payout.paid_at).toLocaleDateString() : new Date(payout.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {pagination && pagination.total_pages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {pagination.page} of {pagination.total_pages} ({pagination.total} total)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm" variant="outline"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => setPage(p => p + 1)}
                      disabled={page >= pagination.total_pages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const AdminDashboard = ({ profile, setProfile }) => {
  const [activeTab, setActiveTab] = useState('war-room');
  
  // Separate loading states for each action
  const [pricingLoading, setPricingLoading] = useState(false);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [logoLoading, setLogoLoading] = useState(false);
  const [expenseLoading, setExpenseLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  
  // Settings state
  const [pricing, setPricing] = useState({
    featured_post: 30,
    boost_post: 15,
    pack50_price: 299,
    pack100_price: 499,
    pack300_price: 999,
    banner_price_3d: 35,
    banner_price_7d: 75,
    banner_price_14d: 130,
    banner_price_30d: 250,
    banner_max_slots: 5,
    banner_show_my_banners: false,
    banner_show_job_create: false,
    banner_show_popup: false,
    banner_show_job_details: false,
    banner_popup_max_shows: 3
  });
  const [appVersion, setAppVersion] = useState('3.1.1');
  
  // Credit addition state
  const [creditForm, setCreditForm] = useState({
    user_id: '',
    amount: '',
    description: ''
  });
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userComboOpen, setUserComboOpen] = useState(false);

  // PESO staff designation state
  const [pesoUserId, setPesoUserId] = useState('');
  const [pesoUserSearch, setPesoUserSearch] = useState('');
  const [pesoComboOpen, setPesoComboOpen] = useState(false);
  const [pesoBusy, setPesoBusy] = useState(false);
  const [pesoStaffOffice, setPesoStaffOffice] = useState('');

  // PESO offices (LGU registry) state
  const [pesoOffices, setPesoOffices] = useState([]);
  const [officeForm, setOfficeForm] = useState({
    slug: '', name: '', lgu_name: '', tagline: '', primary_color: '#1d4ed8',
    logo_url: '', seal_url: '', moa_note: '', barangays: '', categories: '', is_active: true,
  });
  const [officeBusy, setOfficeBusy] = useState(false);

  // Expenses state
  const [expenses, setExpenses] = useState([]);
  const [expenseForm, setExpenseForm] = useState({
    category: 'Marketing',
    description: '',
    amount: '',
    expense_date: getManilaTodayStr()
  });

  // Reports state — 30-day window anchored on Asia/Manila, not the admin's device timezone.
  const [dateRange, setDateRange] = useState({
    start: manilaDaysAgoStr(30),
    end: getManilaTodayStr()
  });
  const [reportData, setReportData] = useState(null);
  
  // Logo state
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    fetchSettings();
    fetchUsers();
    fetchExpenses();
    fetchPesoOffices();
  }, []);

  const fetchPesoOffices = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const response = await api.get('/api/admin/peso/offices', {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      setPesoOffices(response.data.offices || []);
    } catch (error) {
      console.error('Error fetching PESO offices:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      
      const response = await api.get('/api/admin/settings', {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      
      // Merge (not replace) so keys the stored config doesn't have yet (e.g. newly
      // added banner feature flags) keep their safe local defaults instead of going blank.
      setPricing((prev) => ({ ...prev, ...response.data.pricing }));
      if (response.data.logo_url) {
        setLogoPreview(response.data.logo_url);
      }
      if (response.data.app_version) {
        setAppVersion(response.data.app_version);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      
      const response = await api.get('/api/admin/users', {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchExpenses = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      
      const response = await api.get('/api/admin/expenses', {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      
      // Sort expenses by created_at timestamp (newest first)
      const sortedExpenses = (response.data.expenses || []).sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });
      
      setExpenses(sortedExpenses);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  const fetchReports = async () => {
    try {
      setReportLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      
      const response = await api.get('/api/admin/reports', {
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end
        },
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setReportLoading(false);
    }
  };

  const updatePricing = async () => {
    try {
      setPricingLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Not authenticated');
        return;
      }
      
      const response = await api.put('/api/admin/settings', {
        ...pricing,
        app_version: appVersion
      }, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      
      if (response.data && response.data.success) {
        // Update local state with server response (merge to keep any keys the response omits)
        setPricing((prev) => ({ ...prev, ...response.data.pricing }));
        if (response.data.app_version) {
          setAppVersion(response.data.app_version);
        }
        // CRITICAL FIX: Refresh settings from database to verify persistence
        await fetchSettings();
        toast.success('Settings updated successfully');
        console.log('Settings updated:', response.data);
      } else {
        toast.error('Failed to update settings - invalid response');
      }
    } catch (error) {
      console.error('Error updating pricing:', error);
      toast.error(error.response?.data?.detail || 'Failed to update pricing');
    } finally {
      setPricingLoading(false);
    }
  };

  const addCreditsToUser = async () => {
    if (!creditForm.user_id || !creditForm.amount) {
      toast.error('Please select a user and enter amount');
      return;
    }
    
    try {
      setCreditsLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      
      await api.post('/api/admin/credits/add', {
        user_id: creditForm.user_id,
        amount: parseInt(creditForm.amount),
        description: creditForm.description
      }, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      
      toast.success('Credits added successfully');
      setCreditForm({ user_id: '', amount: '', description: '' });
    } catch (error) {
      console.error('Error adding credits:', error);
      toast.error(error.response?.data?.detail || 'Failed to add credits');
    } finally {
      setCreditsLoading(false);
    }
  };

  const setPesoStaff = async (makeStaff) => {
    if (!pesoUserId) {
      toast.error('Please select a user');
      return;
    }
    if (makeStaff && !pesoStaffOffice) {
      toast.error('Please select a PESO office');
      return;
    }
    try {
      setPesoBusy(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const path = makeStaff
        ? `/api/admin/make-peso-staff/${pesoUserId}`
        : `/api/admin/revoke-peso-staff/${pesoUserId}`;
      const payload = makeStaff ? { peso_office: pesoStaffOffice } : {};
      const res = await api.post(path, payload, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      toast.success(res.data?.message || (makeStaff ? 'PESO staff granted' : 'PESO staff revoked'));
      setPesoUserId('');
      fetchUsers();
    } catch (error) {
      console.error('Error updating PESO staff:', error);
      toast.error(error.response?.data?.detail || 'Failed to update PESO staff');
    } finally {
      setPesoBusy(false);
    }
  };

  const saveOffice = async () => {
    const slug = (officeForm.slug || '').trim().toLowerCase();
    if (!slug || !officeForm.name.trim()) {
      toast.error('Slug and name are required');
      return;
    }
    try {
      setOfficeBusy(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      // barangays: newline/comma separated -> array
      const barangays = (officeForm.barangays || '')
        .split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
      // categories: each line "Label | BT Category" -> {label, bt_category}
      const categories = (officeForm.categories || '')
        .split(/\n+/).map((line) => {
          const [label, bt] = line.split('|').map((s) => (s || '').trim());
          return label ? { label, bt_category: bt || 'Other' } : null;
        }).filter(Boolean);
      const body = {
        slug,
        name: officeForm.name.trim(),
        lgu_name: officeForm.lgu_name.trim(),
        tagline: officeForm.tagline.trim(),
        primary_color: officeForm.primary_color.trim() || '#1d4ed8',
        logo_url: officeForm.logo_url.trim(),
        seal_url: officeForm.seal_url.trim(),
        moa_note: officeForm.moa_note.trim(),
        barangays,
        categories,
        is_active: !!officeForm.is_active,
      };
      await api.post('/api/admin/peso/offices', body, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      toast.success(`Saved PESO office "${slug}"`);
      setOfficeForm({ slug: '', name: '', lgu_name: '', tagline: '', primary_color: '#1d4ed8', logo_url: '', seal_url: '', moa_note: '', barangays: '', categories: '', is_active: true });
      fetchPesoOffices();
    } catch (error) {
      console.error('Error saving PESO office:', error);
      toast.error(error.response?.data?.detail || 'Failed to save office');
    } finally {
      setOfficeBusy(false);
    }
  };

  const editOffice = (o) => {
    setOfficeForm({
      slug: o.slug || '',
      name: o.name || '',
      lgu_name: o.lgu_name || '',
      tagline: o.tagline || '',
      primary_color: o.primary_color || '#1d4ed8',
      logo_url: o.logo_url || '',
      seal_url: o.seal_url || '',
      moa_note: o.moa_note || '',
      barangays: Array.isArray(o.barangays) ? o.barangays.join(', ') : '',
      categories: Array.isArray(o.categories) ? o.categories.map((c) => `${c.label} | ${c.bt_category || 'Other'}`).join('\n') : '',
      is_active: o.is_active !== false,
    });
  };

  const addExpense = async () => {
    if (!expenseForm.description || !expenseForm.amount) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setExpenseLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      
      await api.post('/api/admin/expenses', {
        category: expenseForm.category,
        description: expenseForm.description,
        amount: parseFloat(expenseForm.amount),
        expense_date: expenseForm.expense_date
      }, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      
      toast.success('Expense added successfully');
      setExpenseForm({
        category: 'Marketing',
        description: '',
        amount: '',
        expense_date: getManilaTodayStr()
      });
      fetchExpenses();
    } catch (error) {
      console.error('Error adding expense:', error);
      toast.error('Failed to add expense');
    } finally {
      setExpenseLoading(false);
    }
  };

  const deleteExpense = async (expenseId) => {
    if (!confirm('Are you sure you want to delete this expense?')) return;
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      
      await api.delete(`/api/admin/expenses/${expenseId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      
      toast.success('Expense deleted');
      fetchExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File size must be less than 20MB');
      return;
    }
    
    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadLogo = async () => {
    if (!logoFile) {
      toast.error('Please select a logo file first');
      return;
    }
    
    try {
      setLogoLoading(true);
      setUploadProgress(0);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      
      // Compress logo image before uploading
      const compressed = await compressImage(logoFile, 'avatar');

      const formData = new FormData();
      formData.append('file', compressed);

      await api.post('/api/admin/logo/upload', formData, {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        },
        timeout: UPLOAD_TIMEOUT,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      toast.success('Logo uploaded successfully! Refresh the page to see changes.');
      setLogoFile(null);
      setLogoPreview('');
      setUploadProgress(0);
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
      setUploadProgress(0);
    } finally {
      setLogoLoading(false);
    }
  };

  const exportReportToPDF = () => {
    if (!reportData) {
      toast.error('Please generate a report first');
      return;
    }
    
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(20);
    doc.text('Bohol Trabaho - Financial Report', 20, 20);
    
    // Date Range
    doc.setFontSize(12);
    doc.text(`Period: ${dateRange.start} to ${dateRange.end}`, 20, 30);
    
    // Summary (FIXED: Use PHP instead of peso symbol to avoid encoding errors)
    doc.setFontSize(14);
    doc.text('Summary', 20, 45);
    doc.setFontSize(10);
    doc.text(`Total Revenue: PHP ${reportData.summary.total_revenue.toFixed(2)}`, 20, 55);
    doc.text(`Total Expenses: PHP ${reportData.summary.total_expenses.toFixed(2)}`, 20, 62);
    doc.text(`Net Profit: PHP ${reportData.summary.net_profit.toFixed(2)}`, 20, 69);
    
    // Expenses by Category
    doc.setFontSize(14);
    doc.text('Expenses by Category', 20, 85);
    
    const expenseData = Object.entries(reportData.expenses_by_category).map(([category, amount]) => [
      category,
      `PHP ${amount.toFixed(2)}`
    ]);
    
    autoTable(doc, {
      startY: 90,
      head: [['Category', 'Amount']],
      body: expenseData
    });
    
    // Expenses List
    doc.addPage();
    doc.setFontSize(14);
    doc.text('Expense Details', 20, 20);
    
    const expensesList = reportData.expenses.map(exp => [
      exp.expense_date,
      exp.category,
      exp.description,
      `PHP ${parseFloat(exp.amount).toFixed(2)}`
    ]);
    
    autoTable(doc, {
      startY: 25,
      head: [['Date', 'Category', 'Description', 'Amount']],
      body: expensesList
    });
    
    doc.save(`report_${dateRange.start}_${dateRange.end}.pdf`);
    toast.success('Report exported successfully');
  };

  useEffect(() => {
    if (activeTab === 'reports') {
      fetchReports();
    }
  }, [activeTab]);

  return (
    <div className="min-h-screen-safe pb-nav md:pb-0" data-testid="admin-dashboard">
      <TopBar profile={profile} setProfile={setProfile} />

      <div className="px-4 py-6 max-w-7xl mx-auto">
        <h1 className="font-heading text-3xl font-bold mb-6">Admin Dashboard</h1>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full h-auto grid-cols-2 sm:grid-cols-3 md:grid-cols-9">
            <TabsTrigger value="war-room" data-testid="war-room-tab">CEO War Room</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab">Analytics</TabsTrigger>
            <TabsTrigger value="marketing" data-testid="marketing-tab">Marketing</TabsTrigger>
            <TabsTrigger value="community" data-testid="community-tab">Community</TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab">Settings</TabsTrigger>
            <TabsTrigger value="expenses" data-testid="expenses-tab">Expenses</TabsTrigger>
            <TabsTrigger value="reports" data-testid="reports-tab">Reports</TabsTrigger>
            <TabsTrigger value="referrals" data-testid="referrals-tab">Referrals</TabsTrigger>
            <TabsTrigger value="activity" data-testid="activity-tab">Activity</TabsTrigger>
          </TabsList>

          {/* CEO War Room Tab */}
          <TabsContent value="war-room" className="space-y-6">
            <CEODashboard profile={profile} setProfile={setProfile} hideNavigation={true} />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <PlatformAnalytics />
          </TabsContent>

          {/* Marketing Tab */}
          <TabsContent value="marketing" className="space-y-6">
            <MarketingDashboard />
          </TabsContent>

          {/* Community Board Tab */}
          <TabsContent value="community" className="space-y-6">
            <CommunityBoardAdmin />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            {/* Pricing Configuration */}
            <Card>
              <CardHeader>
                <CardTitle>Pricing Configuration</CardTitle>
                <CardDescription>Set credit costs for different actions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Featured Post</Label>
                    <Input
                      type="number"
                      value={pricing.featured_post}
                      onChange={(e) => setPricing({ ...pricing, featured_post: parseInt(e.target.value) })}
                      data-testid="featured-post-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Boost Post</Label>
                    <Input
                      type="number"
                      value={pricing.boost_post}
                      onChange={(e) => setPricing({ ...pricing, boost_post: parseInt(e.target.value) })}
                      data-testid="boost-post-input"
                    />
                  </div>
                </div>
                
                <div className="mt-6">
                  <h4 className="text-sm font-semibold mb-3">Credit Package Prices (PHP)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>50 Credits Package</Label>
                      <Input
                        type="number"
                        value={pricing.pack50_price}
                        onChange={(e) => setPricing({ ...pricing, pack50_price: parseInt(e.target.value) || 0 })}
                        placeholder="299"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>100 Credits Package</Label>
                      <Input
                        type="number"
                        value={pricing.pack100_price}
                        onChange={(e) => setPricing({ ...pricing, pack100_price: parseInt(e.target.value) || 0 })}
                        placeholder="499"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>300 Credits Package</Label>
                      <Input
                        type="number"
                        value={pricing.pack300_price}
                        onChange={(e) => setPricing({ ...pricing, pack300_price: parseInt(e.target.value) || 0 })}
                        placeholder="999"
                      />
                    </div>

                  </div>
                </div>

                <div className="mt-6 border-t pt-6">
                  <h4 className="text-sm font-semibold mb-3">Banner ads</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
                    <div className="space-y-2">
                      <Label>3-Day Price (credits)</Label>
                      <Input
                        type="number"
                        value={pricing.banner_price_3d}
                        onChange={(e) => setPricing({ ...pricing, banner_price_3d: parseInt(e.target.value) || 0 })}
                        placeholder="35"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>7-Day Price (credits)</Label>
                      <Input
                        type="number"
                        value={pricing.banner_price_7d}
                        onChange={(e) => setPricing({ ...pricing, banner_price_7d: parseInt(e.target.value) || 0 })}
                        placeholder="75"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>14-Day Price (credits)</Label>
                      <Input
                        type="number"
                        value={pricing.banner_price_14d}
                        onChange={(e) => setPricing({ ...pricing, banner_price_14d: parseInt(e.target.value) || 0 })}
                        placeholder="130"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>30-Day Price (credits)</Label>
                      <Input
                        type="number"
                        value={pricing.banner_price_30d}
                        onChange={(e) => setPricing({ ...pricing, banner_price_30d: parseInt(e.target.value) || 0 })}
                        placeholder="250"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Max Concurrent Slots</Label>
                      <Input
                        type="number"
                        value={pricing.banner_max_slots}
                        onChange={(e) => setPricing({ ...pricing, banner_max_slots: parseInt(e.target.value) || 1 })}
                        placeholder="5"
                      />
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground mb-3">
                    These surfaces are OFF by default. Enable one at a time to control the rollout.
                  </p>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <Label htmlFor="banner-show-my-banners" className="cursor-pointer">
                        Show "My Banners" section (employer dashboard)
                      </Label>
                      <Switch
                        id="banner-show-my-banners"
                        checked={!!pricing.banner_show_my_banners}
                        onCheckedChange={(checked) => setPricing({ ...pricing, banner_show_my_banners: checked })}
                        data-testid="banner-show-my-banners-toggle"
                      />
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <Label htmlFor="banner-show-job-create" className="cursor-pointer">
                        Show banner offer on job-create page
                      </Label>
                      <Switch
                        id="banner-show-job-create"
                        checked={!!pricing.banner_show_job_create}
                        onCheckedChange={(checked) => setPricing({ ...pricing, banner_show_job_create: checked })}
                        data-testid="banner-show-job-create-toggle"
                      />
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <Label htmlFor="banner-show-job-details" className="cursor-pointer">
                        Show banner prompt on job details (applicants view)
                      </Label>
                      <Switch
                        id="banner-show-job-details"
                        checked={!!pricing.banner_show_job_details}
                        onCheckedChange={(checked) => setPricing({ ...pricing, banner_show_job_details: checked })}
                        data-testid="banner-show-job-details-toggle"
                      />
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <Label htmlFor="banner-show-popup" className="cursor-pointer">
                        Show one-time intro pop-up
                      </Label>
                      <Switch
                        id="banner-show-popup"
                        checked={!!pricing.banner_show_popup}
                        onCheckedChange={(checked) => setPricing({ ...pricing, banner_show_popup: checked })}
                        data-testid="banner-show-popup-toggle"
                      />
                    </div>

                    <div className="space-y-2 max-w-xs">
                      <Label>Pop-up Max Shows (per browser)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="20"
                        value={pricing.banner_popup_max_shows}
                        onChange={(e) => setPricing({ ...pricing, banner_popup_max_shows: parseInt(e.target.value) || 0 })}
                        placeholder="3"
                        data-testid="banner-popup-max-shows-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>App Version (Footer)</Label>
                  <Input
                    type="text"
                    value={appVersion}
                    onChange={(e) => setAppVersion(e.target.value)}
                    placeholder="e.g., 3.1.1"
                    data-testid="app-version-input"
                  />
                  <p className="text-xs text-muted-foreground">This version will be displayed in the footer of all pages</p>
                </div>
                
                <Button onClick={updatePricing} disabled={pricingLoading} data-testid="update-pricing-btn">
                  {pricingLoading ? 'Updating...' : 'Update Settings'}
                </Button>
              </CardContent>
            </Card>

            {/* Manual Credit Addition */}
            <Card>
              <CardHeader>
                <CardTitle>Add Credits to User</CardTitle>
                <CardDescription>Manually award credits to employer/owner accounts</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Search User</Label>
                    <Popover open={userComboOpen} onOpenChange={setUserComboOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={userComboOpen}
                          className="w-full justify-between"
                          data-testid="user-search"
                        >
                          {creditForm.user_id
                            ? users.find((user) => user.id === creditForm.user_id)?.name || "Select user..."
                            : "Search and select user..."}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0">
                        <Command>
                          <CommandInput placeholder="Search by name or email..." value={userSearch} onValueChange={setUserSearch} />
                          <CommandEmpty>No user found.</CommandEmpty>
                          <CommandGroup className="max-h-64 overflow-auto">
                            {users
                              .filter(user => 
                                user.name.toLowerCase().includes(userSearch.toLowerCase()) ||
                                user.email.toLowerCase().includes(userSearch.toLowerCase())
                              )
                              .map((user) => (
                                <CommandItem
                                  key={user.id}
                                  value={user.id}
                                  onSelect={() => {
                                    setCreditForm({ ...creditForm, user_id: user.id });
                                    setUserComboOpen(false);
                                    setUserSearch('');
                                  }}
                                >
                                  <div className="flex flex-col">
                                    <span className="font-medium">{user.name}</span>
                                    <span className="text-sm text-muted-foreground">{user.email} - {user.role}</span>
                                  </div>
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Amount</Label>
                    <Input
                      type="number"
                      placeholder="Enter credit amount"
                      value={creditForm.amount}
                      onChange={(e) => setCreditForm({ ...creditForm, amount: e.target.value })}
                      data-testid="credit-amount-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Description (optional)</Label>
                  <Input
                    placeholder="Reason for adding credits"
                    value={creditForm.description}
                    onChange={(e) => setCreditForm({ ...creditForm, description: e.target.value })}
                    data-testid="credit-description-input"
                  />
                </div>
                
                {creditsLoading && (
                  <div className="space-y-2">
                    <Progress value={100} className="h-2" data-testid="credits-loading-progress" />
                    <p className="text-sm text-muted-foreground text-center animate-pulse">
                      Processing credit addition...
                    </p>
                  </div>
                )}
                
                <Button onClick={addCreditsToUser} disabled={creditsLoading} data-testid="add-credits-btn">
                  <Plus size={16} className="mr-2" />
                  {creditsLoading ? 'Adding...' : 'Add Credits'}
                </Button>
              </CardContent>
            </Card>

            {/* PESO Staff Management */}
            <Card>
              <CardHeader>
                <CardTitle>PESO Staff</CardTitle>
                <CardDescription>Designate a Panglao PESO staff account. PESO staff get their own isolated dashboard (approve employers, verify in-person seekers, view analytics) and no marketplace/admin access. The user must sign up normally first, then be promoted here.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Search User</Label>
                  <Popover open={pesoComboOpen} onOpenChange={setPesoComboOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={pesoComboOpen}
                        className="w-full justify-between"
                        data-testid="peso-user-search"
                      >
                        {pesoUserId
                          ? users.find((user) => user.id === pesoUserId)?.name || "Select user..."
                          : "Search and select user..."}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0">
                      <Command>
                        <CommandInput placeholder="Search by name or email..." value={pesoUserSearch} onValueChange={setPesoUserSearch} />
                        <CommandEmpty>No user found.</CommandEmpty>
                        <CommandGroup className="max-h-64 overflow-auto">
                          {users
                            .filter(user =>
                              user.name.toLowerCase().includes(pesoUserSearch.toLowerCase()) ||
                              user.email.toLowerCase().includes(pesoUserSearch.toLowerCase())
                            )
                            .map((user) => (
                              <CommandItem
                                key={user.id}
                                value={user.id}
                                onSelect={() => {
                                  setPesoUserId(user.id);
                                  setPesoComboOpen(false);
                                  setPesoUserSearch('');
                                }}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{user.name}</span>
                                  <span className="text-sm text-muted-foreground">{user.email} - {user.role}</span>
                                </div>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {pesoUserId && (
                  <p className="text-sm text-muted-foreground">
                    Current role: <span className="font-medium">{users.find((u) => u.id === pesoUserId)?.role || '—'}</span>
                  </p>
                )}

                <div className="space-y-2">
                  <Label>PESO Office (LGU)</Label>
                  <select
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={pesoStaffOffice}
                    onChange={(e) => setPesoStaffOffice(e.target.value)}
                    data-testid="peso-staff-office"
                  >
                    <option value="">Select office…</option>
                    {pesoOffices.filter((o) => o.is_active !== false).map((o) => (
                      <option key={o.slug} value={o.slug}>{o.name} ({o.slug})</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => setPesoStaff(true)}
                    disabled={pesoBusy || !pesoUserId || !pesoStaffOffice}
                    data-testid="make-peso-staff-btn"
                  >
                    {pesoBusy ? 'Working…' : 'Make / Reassign PESO Staff'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setPesoStaff(false)}
                    disabled={pesoBusy || !pesoUserId || users.find((u) => u.id === pesoUserId)?.role !== 'peso_staff'}
                    data-testid="revoke-peso-staff-btn"
                  >
                    Revoke
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* PESO Offices (LGU registry) */}
            <Card>
              <CardHeader>
                <CardTitle>PESO Offices (LGU)</CardTitle>
                <CardDescription>Onboard a new LGU PESO without a code deploy. Each office gets its own branded portal at <code>/peso/&lt;slug&gt;</code> and scopes its staff + registrants. Editing a slug that exists updates it.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {pesoOffices.length > 0 && (
                  <div className="rounded-md border divide-y">
                    {pesoOffices.map((o) => (
                      <div key={o.slug} className="flex items-center justify-between px-3 py-2 text-sm">
                        <div>
                          <span className="font-medium">{o.name}</span>{' '}
                          <span className="text-muted-foreground">/peso/{o.slug}</span>
                          {o.is_active === false && <Badge variant="secondary" className="ml-2">inactive</Badge>}
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => editOffice(o)}>Edit</Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Slug (URL) *</Label>
                    <Input placeholder="e.g. tagbilaran" value={officeForm.slug} onChange={(e) => setOfficeForm({ ...officeForm, slug: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label>Office Name *</Label>
                    <Input placeholder="e.g. PESO Tagbilaran" value={officeForm.name} onChange={(e) => setOfficeForm({ ...officeForm, name: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label>LGU Name</Label>
                    <Input placeholder="e.g. City of Tagbilaran, Bohol" value={officeForm.lgu_name} onChange={(e) => setOfficeForm({ ...officeForm, lgu_name: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label>Tagline</Label>
                    <Input placeholder="Public Employment Service Office" value={officeForm.tagline} onChange={(e) => setOfficeForm({ ...officeForm, tagline: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label>Primary Color</Label>
                    <Input placeholder="#1d4ed8" value={officeForm.primary_color} onChange={(e) => setOfficeForm({ ...officeForm, primary_color: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label>Logo URL</Label>
                    <Input placeholder="/peso-logo.png or https://…" value={officeForm.logo_url} onChange={(e) => setOfficeForm({ ...officeForm, logo_url: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label>Seal URL</Label>
                    <Input placeholder="/lgu-seal.png or https://…" value={officeForm.seal_url} onChange={(e) => setOfficeForm({ ...officeForm, seal_url: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label>MOA Note (footer)</Label>
                    <Input placeholder="MOA signed …" value={officeForm.moa_note} onChange={(e) => setOfficeForm({ ...officeForm, moa_note: e.target.value })} />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label>Barangays (comma or newline separated)</Label>
                  <textarea rows={2} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    placeholder="Cogon, Dao, Poblacion I, …"
                    value={officeForm.barangays} onChange={(e) => setOfficeForm({ ...officeForm, barangays: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label>Job Categories — one per line as <code>Label | BT Category</code></Label>
                  <textarea rows={4} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono text-xs"
                    placeholder={"Tourism & Hospitality Services | Hospitality & Tourism\nRetail & Customer Service | Retail\nOthers | Other"}
                    value={officeForm.categories} onChange={(e) => setOfficeForm({ ...officeForm, categories: e.target.value })} />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={officeForm.is_active} onChange={(e) => setOfficeForm({ ...officeForm, is_active: e.target.checked })} />
                  Active (visible at /peso/&lt;slug&gt;)
                </label>

                <Button onClick={saveOffice} disabled={officeBusy}>
                  {officeBusy ? 'Saving…' : 'Save Office'}
                </Button>
              </CardContent>
            </Card>

            {/* Logo Upload */}
            <Card>
              <CardHeader>
                <CardTitle>App Logo</CardTitle>
                <CardDescription>Upload a new logo (max 2MB, PNG/JPG/WEBP)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {logoPreview && (
                  <div className="flex items-center gap-4">
                    <img src={logoPreview} alt="Logo preview" className="h-20 w-auto" />
                  </div>
                )}
                
                <div className="space-y-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    data-testid="logo-upload-input"
                    disabled={logoLoading}
                  />
                </div>
                
                {logoLoading && uploadProgress > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Uploading...</span>
                      <span className="font-medium">{uploadProgress}%</span>
                    </div>
                    <Progress value={uploadProgress} className="w-full" />
                  </div>
                )}
                
                <Button onClick={uploadLogo} disabled={logoLoading || !logoFile} data-testid="upload-logo-btn">
                  <Upload size={16} className="mr-2" />
                  {logoLoading ? `Uploading... ${uploadProgress}%` : 'Upload Logo'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Expenses Tab */}
          <TabsContent value="expenses" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Add New Expense</CardTitle>
                <CardDescription>Log a business expense</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={expenseForm.category} onValueChange={(value) => setExpenseForm({ ...expenseForm, category: value })}>
                      <SelectTrigger data-testid="expense-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Marketing">Marketing</SelectItem>
                        <SelectItem value="Operations">Operations</SelectItem>
                        <SelectItem value="Infrastructure">Infrastructure</SelectItem>
                        <SelectItem value="Salaries">Salaries</SelectItem>
                        <SelectItem value="Utilities">Utilities</SelectItem>
                        <SelectItem value="Legal">Legal</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Amount (₱)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={expenseForm.amount}
                      onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                      data-testid="expense-amount-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={expenseForm.expense_date}
                      onChange={(e) => setExpenseForm({ ...expenseForm, expense_date: e.target.value })}
                      data-testid="expense-date-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    placeholder="Enter expense description"
                    value={expenseForm.description}
                    onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                    data-testid="expense-description-input"
                  />
                </div>
                
                <Button onClick={addExpense} disabled={expenseLoading} data-testid="add-expense-btn">
                  <Plus size={16} className="mr-2" />
                  {expenseLoading ? 'Adding...' : 'Add Expense'}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Expense History</CardTitle>
                <CardDescription>View and manage all expenses</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Added</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expenses.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground">
                            No expenses recorded yet
                          </TableCell>
                        </TableRow>
                      ) : (
                        expenses.map(expense => (
                          <TableRow key={expense.id}>
                            <TableCell>
                              {new Date(expense.expense_date).toLocaleDateString('en-US', {
                                month: '2-digit',
                                day: '2-digit',
                                year: 'numeric'
                              })}
                            </TableCell>
                            <TableCell>
                              <span className="px-2 py-1 rounded-full text-xs bg-primary/10 text-primary">
                                {expense.category}
                              </span>
                            </TableCell>
                            <TableCell>{expense.description}</TableCell>
                            <TableCell>PHP {parseFloat(expense.amount).toFixed(2)}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {new Date(expense.created_at).toLocaleString('en-US', {
                                month: '2-digit',
                                day: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteExpense(expense.id)}
                                data-testid={`delete-expense-${expense.id}`}
                              >
                                <Trash2 size={16} className="text-red-600" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Generate Report</CardTitle>
                <CardDescription>Select date range for financial report</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                      data-testid="report-start-date"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                      data-testid="report-end-date"
                    />
                  </div>
                  
                  <div className="flex items-end gap-2">
                    <Button onClick={fetchReports} disabled={reportLoading} className="flex-1" data-testid="generate-report-btn">
                      {reportLoading ? 'Loading...' : 'Generate Report'}
                    </Button>
                    <Button onClick={exportReportToPDF} disabled={!reportData} variant="outline" data-testid="export-pdf-btn">
                      <Download size={16} className="mr-2" />
                      PDF
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {reportData && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-6">
                      <p className="text-sm text-muted-foreground">Total Revenue</p>
                      <p className="text-2xl font-bold text-green-600">₱{reportData.summary.total_revenue.toFixed(2)}</p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-6">
                      <p className="text-sm text-muted-foreground">Total Expenses</p>
                      <p className="text-2xl font-bold text-red-600">₱{reportData.summary.total_expenses.toFixed(2)}</p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-6">
                      <p className="text-sm text-muted-foreground">Net Profit</p>
                      <p className={`text-2xl font-bold ${reportData.summary.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ₱{reportData.summary.net_profit.toFixed(2)}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Expenses by Category</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={Object.entries(reportData.expenses_by_category).map(([name, value]) => ({ name, value }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {Object.entries(reportData.expenses_by_category).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `₱${value.toFixed(2)}`} />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Revenue by Package</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={Object.entries(reportData.revenue_by_package).map(([name, value]) => ({ name, value }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            outerRadius={80}
                            fill="#82ca9d"
                            dataKey="value"
                          >
                            {Object.entries(reportData.revenue_by_package).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `₱${value.toFixed(2)}`} />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals" className="space-y-6">
            <ReferralsTab />
          </TabsContent>
          <TabsContent value="activity" className="space-y-6">
            <EmployerActivityLog />
          </TabsContent>
        </Tabs>
      </div>

      <BottomNav />
    </div>
  );
};

export default AdminDashboard;